/**
 * Safely copies text to the system clipboard.
 * First attempts the modern Clipboard API (navigator.clipboard.writeText),
 * falling back to document.execCommand('copy') via a hidden textarea if unsupported,
 * insecure, or if permission is denied.
 *
 * Never throws or produces unhandled promise rejections.
 *
 * @param {string} text - The text to copy to the clipboard.
 * @returns {Promise<boolean>} True if copying succeeded, false otherwise.
 */
export async function copyToClipboard(text) {
  if (typeof text !== 'string' || !text) {
    return false;
  }

  // 1. Try modern Clipboard API
  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Modern clipboard failed (e.g. NotAllowedError / permission denied); proceed to fallback
    }
  }

  // 2. Fallback to document.execCommand('copy') via temporary textarea
  if (typeof document !== 'undefined' && typeof document.createElement === 'function' && document.body) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      // Prevent zooming or scrolling on mobile/iOS
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.width = '2em';
      textarea.style.height = '2em';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      textarea.setAttribute('readonly', '');
      textarea.style.opacity = '0';

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, text.length);

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (successful) {
        return true;
      }
    } catch {
      // document.execCommand fallback failed
    }
  }

  return false;
}
